import React from 'react';
import noop from 'lodash/noop';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	DEFAULT_ITEM_HEIGHT,
	DEFAULT_LIST_WIDTH,
	type Item,
	type ItemProject,
} from '../../model/table';
import type { TimelineDuration, TimelineWindow, TimelineMode } from '../../model/timeline';
import { DEFAULT_LEVEL } from './common/constants';
import RoadmapStyleMarshalProvider, {
	type Overrides as RoadmapStyleMarshalOverrides,
} from './common/context/roadmap-style-marshal/provider/index.tsx';
import type { CustomMarshal } from './common/context/roadmap-style-marshal/types.tsx';
import ScrollSynchronizerProvider from './common/context/scroll-synchronizer/provider/index.tsx';
import type { TableCommonProps, OnItemExpandChanged } from './common/types';
import type { Components } from './components';
import Main from './main/view.tsx';

type APIProps = {
	// Determines whether to show visual elements derived from the today prop (e.g. TodayMarker)
	isHighlightToday: boolean;
	// Determines whether to hide the custom scrollbars in the chart (scrolling will still be enabled)
	isHideScrollbar: boolean;
	// Determines whether the list can be resized, including reacting to window resizes. The listWidth prop is still respected.
	isResizeEnabled: boolean;
	// Base prefix for all table html element ids and style marshal classes
	scope: string;
	// The items to be shown in the table
	items: Item[];
	// The projects shown in the create item
	projects: ItemProject[];
	// The item that is currently selected in the list and chart. Alters the appearance.
	selectedItemId: IssueId | undefined;
	// Controls the granularity of the items shown in the Chart
	timelineMode: TimelineMode;
	// The range which the Chart is displayed
	timelineDuration: TimelineDuration;
	// The range which the data is fetched. The timelineWindow will either be equal or less than the timelineDuration.
	// Often the timelineDuration is longer than timelineWindow to give some extra space / padding to the UI.
	timelineWindow: TimelineWindow;
	// The position to scroll to in the Chart. Can be used for initial rendering and/or updated based on the timeline mode.
	timelineDefaultPosition: number;
	// Additional configuration for style marshal
	styleMarshal: Partial<{
		custom: CustomMarshal;
		overrides: RoadmapStyleMarshalOverrides;
	}>;
	// Today's date
	today: Date;
} & TableCommonProps & {
		// Optional custom implementations of table components
		components: Partial<Components>;
		// Width of the list component
		listWidth: number;
		// Callback to signal that the Timeline is done updating
		onTimelineUpdated: () => void;
		// Callback to signal that item expansion state was changed,
		onItemExpandChanged: OnItemExpandChanged;
		// Callback to signal a change of width in the list, occurs when a user resizes the list
		onSetListWidth: (width: number) => void;
	};

function Table(props: APIProps) {
	const { scope, styleMarshal, ...mainProps } = props;

	return (
		<RoadmapStyleMarshalProvider scope={scope} {...styleMarshal}>
			<ScrollSynchronizerProvider>
				<Main {...mainProps} />
			</ScrollSynchronizerProvider>
		</RoadmapStyleMarshalProvider>
	);
}

Table.defaultProps = {
	isSelectEnabled: true,
	isHoverEnabled: false,
	isResizeEnabled: true,
	isHighlightToday: true,
	isHideScrollbar: false,
	items: [],
	activeLevel: DEFAULT_LEVEL,
	expandedItems: {},
	components: {},
	styleMarshal: {},
	selectedItemId: undefined,
	itemHeight: DEFAULT_ITEM_HEIGHT,
	listWidth: DEFAULT_LIST_WIDTH,
	onItemClick: noop,
	onItemExpandChanged: noop,
	onTimelineUpdated: noop,
	onSetListWidth: noop,
};

export default Table;
export type { APIProps };
